import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Table } from 'antd';
import { ArrowRightOutlined, FileTextOutlined, SearchOutlined } from '@ant-design/icons';
import { formatRefDate } from '../../utils/formatRefDate';
import useNearestProvinces from './useNearestProvinces';

export default function PositionReports({ reports, loading }) {
    const provinces = useNearestProvinces(reports);
    const [search, setSearch] = useState('');
    const [showAll, setShowAll] = useState(false);
    const visibleReports = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return reports;
        return reports.filter(row => {
            const province = provinces.values[JSON.stringify([row.lat, row.lng])]?.name;
            return [row.ShipName, row.ShipCode, row.KodeCabang, row.reportDate, row.lat, row.lng, province]
                .some(value => String(value || '').toLowerCase().includes(query));
        });
    }, [provinces.values, reports, search]);
    const displayColumns = [
        { title: 'Report Time', dataIndex: 'reportDate', width: 180, render: value => <time className="monitoring-report-time">{formatRefDate(value)}</time> },
        { title: 'Vessel', key: 'vessel', width: 180, render: (_, row) => <strong className="monitoring-report-vessel">{row.ShipName || row.ShipCode || '—'}</strong> },
        {
            title: 'Location', key: 'province', width: 220, render: (_, row) => <span className="monitoring-report-location">
                <strong>{provinces.loading ? 'Loading...' : provinces.values[JSON.stringify([row.lat, row.lng])]?.name || '—'}</strong>
            </span>
        },
        { title: 'Latitude', dataIndex: 'lat', width: 150, render: value => <span className="monitoring-coordinate">{value.toFixed(6)}</span> },
        { title: 'Longitude', dataIndex: 'lng', width: 150, render: value => <span className="monitoring-coordinate">{value.toFixed(6)}</span> },
    ];
    return <section className="monitoring-reports" aria-labelledby="latest-position-title">
        <header className="monitoring-panel-heading monitoring-reports__heading">
            <div className="monitoring-panel-title"><span className="monitoring-section-icon" aria-hidden="true"><FileTextOutlined /></span><div>
                <h2 id="latest-position-title">Latest Position Reports</h2><p>One latest available report per vessel, not event history</p>
            </div></div>
            <div className="monitoring-report-tools">
                <Button type="link" onClick={() => setShowAll(value => !value)}>{showAll ? 'Show fewer' : 'View All Reports'} <ArrowRightOutlined /></Button>
                <Input allowClear prefix={<SearchOutlined />} aria-label="Search position reports" placeholder="Search vessel, location, or coordinate..."
                    value={search} onChange={event => setSearch(event.target.value)} />
            </div>
        </header>
        <Table size="small" columns={displayColumns} dataSource={visibleReports} loading={loading}
            scroll={{ x: 820 }} pagination={{ pageSize: showAll ? Math.max(visibleReports.length, 1) : 10, showSizeChanger: false, size: 'small', showTotal: total => `${total} reports` }}
            locale={{ emptyText: 'No position reports available' }} />
        {provinces.error && <p className="monitoring-note" role="status">Data provinsi tidak dapat dimuat. Muat ulang halaman untuk mencoba lagi.</p>}
        <p className="monitoring-note">Perkiraan provinsi Indonesia terdekat dari koordinat laporan terakhir, bukan batas wilayah laut atau posisi real-time.
            {' '}Batas provinsi: <a href="https://github.com/denyherianto/indonesia-geojson-topojson-maps-with-38-provinces" target="_blank" rel="noreferrer">Deny Herianto</a>
            {' '}(<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">CC BY 4.0</a>).
        </p>
    </section>;
}
PositionReports.propTypes = { reports: PropTypes.array.isRequired, loading: PropTypes.bool };
